'use client'

import { useCallback, useMemo, useRef, useState } from 'react'
import {
  EMPTY_CHIP_STATE,
  birdieChipReducer,
  chipCount,
  totalPotentialWin,
  totalStaked,
  type BirdieChipAction,
  type BirdieChipState,
  type ChipClamp,
  type ChipLimits,
  type ChipNotice,
  type ChipOutcome
} from './birdieChips'

export interface UseBirdieChips {
  state: BirdieChipState
  /** Applies the action and returns the outcome synchronously (for the caller's sound/flying chip). */
  dispatch: (action: BirdieChipAction) => ChipOutcome
  /** Always-current state for async closures (submission, reconcile). */
  stateRef: { current: BirdieChipState }
  totals: { totalStaked: number; totalPotentialWin: number; chipCount: number }
}

/**
 * Thin React skin over `birdieChipReducer`. It is deliberately NOT `useReducer`:
 * the caller needs the outcome — notice key, clamp list, the resulting board —
 * in the same tick it clicks, to start the flying chip and pick the sound,
 * and `useReducer`'s dispatch returns void.
 *
 * `totals` is exactly the payload of the `onStatsChange` contract the bottom
 * bar already speaks — totalStaked, totalPotentialWin, chipCount
 * (`ChipPlacementPanel.tsx:199-203,1125`).
 */
export function useBirdieChips(input: {
  limits: ChipLimits
  multiplierById: ReadonlyMap<string, number>
  /** optionId → canonical key ('IN2' / 'HMH') — which patterns pay that chip. */
  keyById: Readonly<Record<string, string>>
  onNotice: (notice: ChipNotice, clamped?: ChipClamp[]) => void
}): UseBirdieChips {
  const { limits, multiplierById, keyById, onNotice } = input
  const [state, setState] = useState<BirdieChipState>(EMPTY_CHIP_STATE)

  // Latest-value refs: the submission lifecycle reads the board from an async
  // closure and must never see a stale snapshot (the shared panel's
  // `placedChipsRef` pattern, ChipPlacementPanel.tsx:1805-1806), and two
  // dispatches in one tick must both see the first one's result.
  const stateRef = useRef(state)
  stateRef.current = state

  const limitsRef = useRef(limits)
  limitsRef.current = limits

  const dispatch = useCallback(
    (action: BirdieChipAction): ChipOutcome => {
      const outcome = birdieChipReducer(stateRef.current, action, limitsRef.current)
      stateRef.current = outcome.state
      setState(outcome.state)
      if (outcome.notice) onNotice(outcome.notice, outcome.clamped)
      return outcome
    },
    [onNotice]
  )

  const totals = useMemo(
    () => ({
      totalStaked: totalStaked(state.chips),
      totalPotentialWin: totalPotentialWin(state.chips, multiplierById, keyById),
      chipCount: chipCount(state.chips)
    }),
    [keyById, multiplierById, state.chips]
  )

  return { state, dispatch, stateRef, totals }
}
